import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useState} from 'react'
import toast from 'react-hot-toast'
import {useParams} from 'react-router-dom'
import Select from 'react-select'
import {useDebounceCallback} from 'usehooks-ts'

import {ContactPointCodes, RelationshipCodes} from '../../../../../lib/Codes'
import http from '../../../../../lib/http'
import {getPatientName} from '../../../../../selectors/patient'
import {useAppSelector} from '../../../../../store'
import {capitalize} from '../../../../igao/utils'
import {CheckboxInput} from '../../../../shared/FormBuilder'
import {LoadingIndicator} from "../../../../claim-history/simple-table/loading-indicator";

interface Support {
  entries: Array<{
    name: Array<{
      given: string[]
      family: string[]
    }>
    telecom: Array<{
      use: string
      value: string
      system: string
    }>
    relationship: {
      coding: Array<{
        code: string
      }>
    }
  }>
}

interface InviteProps {
  phones: Array<{
    number: string
    phoneType: string
  }>
  declined: boolean
  declinedMobileEnrollment: boolean
}


function useFamilySupport (patientId?: string) {
  return useQuery({
    queryKey: [ 'family-support', patientId ],
    async queryFn () {
      if (!patientId) {
        return
      }

      const data = http.getRequest<Support>(`/patients/${patientId}/lists/relatedpersons`)

      return await data
    }
  })
}

interface SelectType {
  value: {
    name: string
    phone: string
    type: string
    relationship?: string
  }
  label: string
}

export function Invite ({ phones, declined, declinedMobileEnrollment }: InviteProps): JSX.Element {
  const queryClient = useQueryClient()
  const [ accessOpen, setAccessOpen ] = useState<boolean>(true)
  const [ selected, setSelected ] = useState<SelectType | null>()
  const [ loading, setLoading ] = useState<boolean>()
  const [ showPreviewTab, setShowPreviewTab ] = useState<'ios' | 'android'>('ios')
  const { id } = useParams()
  const { data: familySupport } = useFamilySupport(id)
  const name = useAppSelector(getPatientName)
  const setLoadingFalse = useDebounceCallback(() => setLoading(false), 1000)

  const metaQuery = useQuery({
    queryKey: ['invite-meta', id],
    async queryFn({queryKey}) {
      return await http.getRequest<{
        ios: {
          preview: string
          url: string
        }
        android: {
          preview: string
          url: string
        }
      }>(`/patients/${queryKey[1]}/mobile-app-invitation/meta`)
    }
  })

  function onAccessOpenChange (): void {
    setAccessOpen((prev) => !prev)
  }

  let hasEnrollment = false

  async function send (platform: 'ios' | 'android'): Promise<void> {
    setLoading(true)

    try {
      await http.postRequest(`/patients/${id ?? ''}/mobile-app-invitation`, {
        platform,
        phone: selected?.value.phone,
        type: selected?.value.type,
        relationship: selected?.value.relationship,
        patientName: name,
        receiverName: selected?.value.name
      })

      await queryClient.invalidateQueries({ queryKey: [ 'mobile_invited', id ] })

      toast.success('Successfully sent mobile app invitation', { position: 'bottom-center' })
    } catch (e) {
      toast.error('Failed to send mobile app invitation. Please try again later', { position: 'bottom-center' })
    } finally {
      setLoadingFalse()
    }
  }

  const phoneNumbers: SelectType[] = [
    ...(phones?.map((p) => {
      const phoneType = ContactPointCodes.contactPointUse.find((cpu) => cpu.code === p.phoneType)?.label
      return ({
        value: {
          name,
          phone: p.number,
          type: p.phoneType,
          relationship: 'patient'
        },
        label: `${name} (Patient) - ${phoneType ? phoneType + ':' : ''} ${p.number}`
      })
    }) ?? []),
    ...(familySupport?.entries?.flatMap((e) => {
      const name = `${e.name?.[0]?.family[0] ?? ''}, ${e?.name?.[0]?.given[0] ?? ''}`
      const relation = capitalize(
        RelationshipCodes.find((rc: {
          value: string
        }) => rc.value === e.relationship?.coding?.[0]?.code).display ?? ''
      )

      return e.telecom?.filter(t => t.system === 'phone')?.map((t) => ({
        value: {
          name,
          phone: t.value,
          type: t.use,
          relationship: e.relationship?.coding?.[0]?.code
        },
        label: `${name} (${relation}) - ${ContactPointCodes.contactPointUse.find((cpu) => cpu.code === t.use)?.label ?? ''}: ${t.value}`
      })) ?? []
    }) ?? [])
  ]
    .filter((r) => r.value && r.value?.phone?.length > 0)

  return (
    <div className='nh-card nh-invite-card'>
      <div className='nh-invite-head'>
        <button
          className={`nh-invite-left ${declined ? 'is-disabled' : ''}`}
          onClick={onAccessOpenChange}
        >
          <span className='nh-chevron-wrap'>
            <i
              className='fa fa-chevron-right nh-chevron'
              aria-hidden='true'
            />
          </span>
          <h3 className='nh-invite-title'>Mobile App Access</h3>
        </button>

        <div className='nh-invite-right'>
          <CheckboxInput
            label='Patient declined mobile app'
            name='declinedMobileEnrollment'
            disabled={hasEnrollment}
          />
        </div>
      </div>

      <div
        id='invite-access-body'
        className={`nh-invite-body ${accessOpen ? 'open' : 'collapsed'} ${declined ? 'has-warning' : ''}`}
      >

        {declinedMobileEnrollment
          ? (
            <div className='nh-alert nh-alert-warning'>
              <i
                className='fa fa-exclamation-triangle'
                aria-hidden='true'
              />
              <span>
                Mobile app invitations are disabled because the patient has declined mobile app access.
              </span>
            </div>
          )
          : (
            <>
              <div className='nh-ghost-card nh-ghost-blue'>
                <div className='nh-ghost-line'>
                  <i
                    className='fa fa-info-circle'
                    aria-hidden='true'
                  />
                  <span className='nh-ghost-text'>
                    To send a platform-specific mobile app invitation, a valid mobile phone number must be entered and saved in either the Patient Demographics or Family/Support section. Once saved, the phone number will appear in the invitation list for selection.
                  </span>
                </div>
              </div>

              <div className='row'>
                <div
                  className='col-xs-12 col-sm-8 nh-invite-field nh-invite-field--narrow'
                  style={{ maxWidth: '526px' }}
                >

                  <Select
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 }), // sit on top of modals/cards
                      menu: base => ({ ...base, backgroundColor: '#fff' })
                    }}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition='fixed'
                    menuShouldScrollIntoView={false}

                    placeholder='Select Phone Number to Send Invite'
                    value={selected}
                    onChange={(value) => setSelected(value)}
                    options={phoneNumbers}
                  />
                </div>
              </div>

              <div className='nh-invite-actions'>
                <button
                  type='button'
                  className='btn btn-black'
                  disabled={loading ?? !selected}
                  onClick={() => {
                    send('ios')
                      .catch(console.error)
                  }}
                >
                  <i className='fa fa-apple' /> Send iOS mobile app invite
                </button>

                <button
                  type='button'
                  className='btn btn-green'
                  disabled={loading ?? !selected}
                  onClick={() => {
                    send('android')
                      .catch(console.error)
                  }}
                >
                  <i className='fa fa-android' /> Send Android mobile app invite
                </button>

              </div>
              {metaQuery.data && (
                  <div className='nh-preview-panel'>
                    <div className='nh-preview-head'>
                      <div
                          className='nh-tabs'
                          role='tablist'
                          aria-label='SMS preview platform'
                      >
                        <button
                            type='button'
                            className={`nh-tab ${showPreviewTab === 'ios' ? 'is-active' : ''}`}
                            role='tab'
                            onClick={() => setShowPreviewTab('ios')}
                        >
                          iOS Message
                        </button>
                        <button
                            type='button'
                            className={`nh-tab ${showPreviewTab === 'android' ? 'is-active' : ''}`}
                            role='tab'
                            aria-selected={showPreviewTab === 'android'}
                            onClick={() => setShowPreviewTab('android')}
                        >
                          Android Message
                        </button>
                      </div>
                    </div>

                    {showPreviewTab === 'ios'
                        ? (
                            <div className='nh-sms'>
                              <h4>iOS SMS Preview:</h4>
                              <p>
                                <strong>{metaQuery.data.ios.preview}</strong>{' '}
                                <a
                                    href={metaQuery.data.ios.url}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                  {metaQuery.data.ios.url}
                                </a>
                              </p>
                            </div>
                        )
                        : (
                            <div className='nh-sms'>
                              <h4>Android SMS Preview:</h4>
                              <p>
                                <strong>{metaQuery.data.android.preview}</strong>{' '}
                                <a
                                    href={metaQuery.data.android.url}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                  {metaQuery.data.android.url}
                                </a>
                              </p>
                            </div>
                        )}
                  </div>
              )}

              {metaQuery.isLoading && (
                  <LoadingIndicator loading={true}></LoadingIndicator>
              )}

              {!metaQuery.data && !metaQuery.isLoading && (
                  <p>Something went wrong</p>
              )}
            </>
          )}
      </div>
    </div>
  )
}
